import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Crown, Flame, Globe, HeartPulse, TrendingUp, UserPlus } from 'lucide-react';
import {
  Card,
  EmptyState,
  ErrorNote,
  Skeleton,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatNumber, formatRelative } from '../../lib/format';
import type { AppKey, CountryOverview, CountryStats } from '../../lib/types';
import { usersLink } from '../../lib/user-links';
import {
  CountryTable,
  GrowthValue,
  InsightTile,
  ShareBar,
  countryName,
  flagFor,
  formatGrowth,
  formatPercent,
  growth,
  maxBy,
  percent,
  type CountryColumn,
} from './shared';

/** A country row with the rates every column and insight reads. */
interface CountryRow extends CountryStats {
  name: string;
  flag: string;
  share: number;
  premium_rate: number;
  active_rate: number;
  ios_share: number;
  sessions_per_device: number;
  /** Week-over-week change in registrations, `null` with no prior week. */
  growth: number | null;
}

const COLUMNS: CountryColumn<CountryRow>[] = [
  {
    key: 'devices',
    label: 'Devices',
    title: 'Registered devices and share of all',
    sortValue: (row) => row.devices,
    render: (row, maxShare) => (
      <>
        <span className="font-medium text-ink">{formatNumber(row.devices)}</span>
        <ShareBar share={row.share} max={maxShare} />
      </>
    ),
  },
  {
    key: 'new_7d',
    label: 'New 7d',
    title: 'Registered in the last 7 days',
    sortValue: (row) => row.new_7d,
    render: (row) => formatNumber(row.new_7d),
  },
  {
    key: 'growth',
    label: 'WoW',
    title: 'New devices vs the 7 days before',
    sortValue: (row) => row.growth,
    render: (row) => <GrowthValue value={row.growth} />,
  },
  {
    key: 'active_rate',
    label: 'Active 7d',
    title: 'Seen in the last 7 days',
    sortValue: (row) => row.active_rate,
    render: (row) => formatPercent(row.active_rate),
  },
  {
    key: 'premium_rate',
    label: 'Premium',
    title: 'Premium devices and premium rate',
    sortValue: (row) => row.premium_rate,
    render: (row) => (
      <>
        {formatNumber(row.premium)}
        <span className="block text-xs text-ink-3">
          {formatPercent(row.premium_rate)}
        </span>
      </>
    ),
  },
  {
    key: 'trial',
    label: 'Trial',
    title: 'Devices on a trial now',
    sortValue: (row) => row.trial,
    render: (row) => formatNumber(row.trial),
  },
  {
    key: 'ios_share',
    label: 'iOS',
    title: 'Share of devices on iOS',
    sortValue: (row) => row.ios_share,
    render: (row) => formatPercent(row.ios_share),
  },
  {
    key: 'sessions_per_device',
    label: 'Sess/dev',
    title: 'Average sessions per device',
    sortValue: (row) => row.sessions_per_device,
    render: (row) => row.sessions_per_device.toFixed(1),
  },
];

/**
 * Every device ever registered, by country, with rolling 24 h / 7 d / 30 d
 * windows. The backend memoises the breakdown for five minutes, and every
 * rate here is derived in the browser from that one payload.
 */
export function AllTimeView({ appKey }: { appKey: AppKey | undefined }) {
  const stats = useQuery({
    queryKey: ['countries', appKey ?? null],
    queryFn: () =>
      api.get<CountryOverview>('/api/insights/countries', { app_key: appKey }),
    staleTime: 5 * 60_000,
    placeholderData: (previous) => previous,
  });

  const rows = useMemo(
    () => (stats.data ? toRows(stats.data) : []),
    [stats.data],
  );

  if (stats.isError) return <ErrorNote error={stats.error} />;
  if (stats.isPending) return <ViewSkeleton />;

  if (stats.data.totals.devices === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Globe size={28} />}
          title="No devices yet"
          description="Country statistics appear once devices register."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Insights data={stats.data} rows={rows} appKey={appKey} />
      <CountryTable
        title="By country"
        description={`All devices, rolling windows · updated ${formatRelative(stats.data.generated_at)}`}
        rows={rows}
        columns={COLUMNS}
        // The user filter matches a value, so "unknown" has nothing to link to.
        linkFor={(row) =>
          row.country ? usersLink({ app_key: appKey, country: row.country }) : undefined
        }
        mobileValue={(row) => formatNumber(row.devices)}
        mobileSummary={(row) =>
          `${formatPercent(row.share)} of devices · ${formatNumber(row.new_7d)} new 7d (${formatGrowth(row.growth)}) · ` +
          `${formatPercent(row.active_rate)} active · ${formatPercent(row.premium_rate)} premium · ${formatPercent(row.ios_share)} iOS`
        }
      />
    </div>
  );
}

function Insights({
  data,
  rows,
  appKey,
}: {
  data: CountryOverview;
  rows: CountryRow[];
  appKey: AppKey | undefined;
}) {
  const { totals } = data;
  const known = rows.filter((row) => row.country !== null);
  const unknown = rows.find((row) => row.country === null);

  // A rate over a handful of devices is noise; rank only markets big enough
  // for the number to mean something.
  const minSample = Math.max(20, Math.round(totals.devices * 0.005));
  const sizable = known.filter((row) => row.devices >= minSample);

  const top = known[0];
  const topThreeShare = known
    .slice(0, 3)
    .reduce((sum, row) => sum + row.share, 0);
  const bestConversion = maxBy(sizable, (row) => row.premium_rate);
  const mostEngaged = maxBy(sizable, (row) => row.active_rate);
  const fastestGrowing = maxBy(
    known.filter((row) => row.new_7d >= 10 && row.new_prev_7d >= 5),
    (row) => row.growth ?? -Infinity,
  );

  const overallPremium = percent(totals.premium, totals.devices);
  const overallActive = percent(totals.active_7d, totals.devices);
  const overallGrowth = growth(totals.new_7d, totals.new_prev_7d);
  const link = (country: string) => usersLink({ app_key: appKey, country });

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
      <InsightTile
        icon={<Globe size={16} />}
        label="Countries reached"
        value={formatNumber(known.length)}
        caption={
          `Top 3 hold ${formatPercent(topThreeShare)} of devices` +
          (unknown ? ` · ${formatPercent(unknown.share)} unknown` : '')
        }
      />
      <InsightTile
        icon={<Crown size={16} />}
        label="Top market"
        value={top ? `${top.flag} ${top.name}` : '—'}
        caption={
          top &&
          `${formatNumber(top.devices)} devices · ${formatPercent(top.share)} of all`
        }
        to={top?.country ? link(top.country) : undefined}
        tone="brand"
      />
      <InsightTile
        icon={<UserPlus size={16} />}
        label="New in the last 7 days"
        value={formatNumber(totals.new_7d)}
        caption={`${formatGrowth(overallGrowth)} vs the week before · ${formatNumber(totals.new_30d)} in 30 days`}
        to={usersLink({
          app_key: appKey,
          registered_from: new Date(
            Date.parse(data.generated_at) - 7 * 86_400_000,
          ).toISOString(),
        })}
      />
      <InsightTile
        icon={<TrendingUp size={16} />}
        label="Best conversion"
        value={
          bestConversion
            ? `${bestConversion.flag} ${formatPercent(bestConversion.premium_rate)}`
            : '—'
        }
        caption={
          bestConversion
            ? `${bestConversion.name} · premium rate (avg ${formatPercent(overallPremium)})`
            : `Needs a market with ${minSample}+ devices`
        }
        to={bestConversion?.country ? link(bestConversion.country) : undefined}
      />
      <InsightTile
        icon={<Flame size={16} />}
        label="Fastest growing"
        value={
          fastestGrowing
            ? `${fastestGrowing.flag} ${formatGrowth(fastestGrowing.growth)}`
            : '—'
        }
        caption={
          fastestGrowing
            ? `${fastestGrowing.name} · ${formatNumber(fastestGrowing.new_7d)} new vs ${formatNumber(fastestGrowing.new_prev_7d)} the week before`
            : 'Not enough new devices to compare weeks'
        }
        to={fastestGrowing?.country ? link(fastestGrowing.country) : undefined}
      />
      <InsightTile
        icon={<HeartPulse size={16} />}
        label="Most engaged"
        value={
          mostEngaged
            ? `${mostEngaged.flag} ${formatPercent(mostEngaged.active_rate)}`
            : '—'
        }
        caption={
          mostEngaged
            ? `${mostEngaged.name} · active in 7 days (avg ${formatPercent(overallActive)})`
            : `Needs a market with ${minSample}+ devices`
        }
        to={mostEngaged?.country ? link(mostEngaged.country) : undefined}
      />
    </div>
  );
}

export function ViewSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        {Array.from({ length: 6 }, (_, index) => (
          <Skeleton key={index} className="h-[104px]" />
        ))}
      </div>
      <Skeleton className="h-96" />
    </div>
  );
}

function toRows(data: CountryOverview): CountryRow[] {
  const total = data.totals.devices;
  return data.countries.map((row) => ({
    ...row,
    name: countryName(row.country),
    flag: flagFor(row.country),
    share: percent(row.devices, total),
    premium_rate: percent(row.premium, row.devices),
    active_rate: percent(row.active_7d, row.devices),
    ios_share: percent(row.ios, row.devices),
    sessions_per_device: row.devices ? row.sessions / row.devices : 0,
    growth: growth(row.new_7d, row.new_prev_7d),
  }));
}
